import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Plus, Search, Eye, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'react-hot-toast';

const AdminGoodsReceipts = () => {
    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedReceipts, setExpandedReceipts] = useState({});

    useEffect(() => {
        fetchReceipts();
    }, []);

    const fetchReceipts = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/goods-receipts');
            setReceipts(response.data.receipts || response.data || []);
        } catch (error) {
            toast.error('שגיאה בטעינת קבלות סחורה');
        } finally {
            setLoading(false);
        }
    };

    const toggleExpand = async (receiptId) => {
        if (expandedReceipts[receiptId]) {
            setExpandedReceipts(prev => ({ ...prev, [receiptId]: false }));
        } else {
            // If we don't have items loaded for this receipt, we might need to fetch them
            // But the list endpoint might not return items. Let's check.
            // If the list endpoint returns items, we are good. If not, we fetch details.
            // Based on previous code, list endpoint includes items? No, it includes supplier, PO, receiver.
            // Detail endpoint includes items.

            try {
                const response = await axios.get(`/api/goods-receipts/${receiptId}`);
                setExpandedReceipts(prev => ({
                    ...prev,
                    [receiptId]: response.data // Store full object with items
                }));
            } catch (error) {
                toast.error('שגיאה בטעינת פרטי קבלה');
            }
        }
    };

    const filteredReceipts = receipts.filter(receipt =>
        receipt.receipt_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        receipt.supplier?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        receipt.reference_number?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6" dir="rtl">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-900">קבלות סחורה</h1>
                <Link
                    to="/admin/goods-receipts/new"
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Plus className="w-5 h-5 ml-2" />
                    קבלת סחורה חדשה
                </Link>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
                <div className="relative mb-4">
                    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="חפש לפי מספר קבלה, אסמכתא או ספק..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                </div>

                {loading ? (
                    <div className="text-center py-8">טוען...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-10"></th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">מספר קבלה</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">אסמכתא</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">ספק</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">הזמנת רכש</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">תאריך קבלה</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">התקבל ע"י</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredReceipts.map((receipt) => (
                                    <React.Fragment key={receipt.id}>
                                        <tr className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <button
                                                    onClick={() => toggleExpand(receipt.id)}
                                                    className="text-gray-500 hover:text-gray-700"
                                                >
                                                    {expandedReceipts[receipt.id] ? (
                                                        <ChevronUp className="w-5 h-5" />
                                                    ) : (
                                                        <ChevronDown className="w-5 h-5" />
                                                    )}
                                                </button>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap font-medium text-blue-600">
                                                {receipt.receipt_number}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {receipt.reference_number || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {receipt.supplier?.name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                                                {receipt.purchaseOrder ? (
                                                    <Link to={`/admin/purchase-orders/${receipt.purchaseOrder.id}`}>
                                                        {receipt.purchaseOrder.order_number}
                                                    </Link>
                                                ) : '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {new Date(receipt.received_date).toLocaleDateString('he-IL')}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {receipt.receiver ? `${receipt.receiver.first_name} ${receipt.receiver.last_name}` : '-'}
                                            </td>
                                        </tr>
                                        {expandedReceipts[receipt.id] && (
                                            <tr>
                                                <td colSpan="7" className="bg-gray-50 px-6 py-4">
                                                    <div className="text-sm font-medium text-gray-900 mb-2">פריטים שהתקבלו:</div>
                                                    <table className="min-w-full divide-y divide-gray-200 bg-white rounded-lg overflow-hidden">
                                                        <thead className="bg-gray-100">
                                                            <tr>
                                                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">מוצר</th>
                                                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">כמות</th>
                                                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">עלות יח'</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-200">
                                                            {expandedReceipts[receipt.id].items?.map((item, idx) => (
                                                                <tr key={idx}>
                                                                    <td className="px-4 py-2 text-sm">{item.product?.name}</td>
                                                                    <td className="px-4 py-2 text-sm">{item.quantity_received}</td>
                                                                    <td className="px-4 py-2 text-sm">₪{item.unit_cost}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminGoodsReceipts;
